/**
 * Tag Input Validators
 *
 * Predefined validators for common tag input scenarios.
 * Validators can be passed as functions or regex patterns.
 *
 * Usage:
 * ```typescript
 * // Using predefined validator
 * <app-tag-input [validation]="validators.email" />
 *
 * // Using regex validator
 * <app-tag-input [validation]="validators.createRegexValidator(/^[A-Z]+$/, 'Must be uppercase')" />
 *
 * // Using custom function
 * <app-tag-input [validation]="myCustomValidator" />
 * ```
 */

/**
 * A validator function that returns:
 * - true: valid
 * - null/false: invalid (generic message)
 * - string: invalid with custom error message
 */
export type ValidatorFn = (item: string) => boolean | string | null;

/**
 * Validator input - accepts both regex patterns and validator functions
 */
export type ValidatorInput = RegExp | ValidatorFn;

/**
 * Predefined validators for common use cases
 */
export const validators = {
  /**
   * Email address validator
   * Validates: user@example.com, test.email+tag@domain.co.uk
   */
  email: ((value: string): boolean | string => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return 'Invalid email format';
    }
    return true;
  }) as ValidatorFn,

  /**
   * URL validator
   * Validates: https://example.com, http://example.co.uk
   */
  url: ((value: string): boolean | string => {
    try {
      new URL(value);
      return true;
    } catch {
      return 'Invalid URL format';
    }
  }) as ValidatorFn,

  /**
   * Alphanumeric validator (letters and numbers only)
   */
  alphanumeric: ((value: string): boolean | string => {
    if (!/^[a-zA-Z0-9]+$/.test(value)) {
      return 'Only letters and numbers allowed';
    }
    return true;
  }) as ValidatorFn,

  /**
   * No spaces validator
   */
  noSpaces: ((value: string): boolean | string => {
    if (/\s/.test(value)) {
      return 'Spaces not allowed';
    }
    return true;
  }) as ValidatorFn,

  /**
   * Min length validator
   */
  minLength: (min: number): ValidatorFn => {
    return (value: string): boolean | string => {
      if (value.length < min) {
        return `Minimum ${min} characters required`;
      }
      return true;
    };
  },

  /**
   * Max length validator
   */
  maxLength: (max: number): ValidatorFn => {
    return (value: string): boolean | string => {
      if (value.length > max) {
        return `Maximum ${max} characters allowed`;
      }
      return true;
    };
  },

  /**
   * Create a regex-based validator
   * @param pattern RegExp to match against
   * @param errorMessage Custom error message if validation fails
   * @returns ValidatorFn that validates against the pattern
   */
  createRegexValidator: (
    pattern: RegExp,
    errorMessage = 'Invalid format',
  ): ValidatorFn => {
    return (value: string): boolean | string => {
      if (!pattern.test(value)) {
        return errorMessage;
      }
      return true;
    };
  },

  /**
   * Compose multiple validators (all must pass)
   */
  compose: (...validators: ValidatorFn[]): ValidatorFn => {
    return (value: string): boolean | string => {
      for (const validator of validators) {
        const result = validator(value);
        if (result !== true && result !== null) {
          return result;
        }
      }
      return true;
    };
  },

  /**
   * Convert a regex pattern to a validator function
   * @param pattern RegExp pattern
   * @param errorMessage Optional error message
   * @returns ValidatorFn
   */
  fromRegex: (
    pattern: RegExp,
    errorMessage = 'Invalid format',
  ): ValidatorFn => {
    return (value: string): boolean | string => {
      if (!pattern.test(value)) {
        return errorMessage;
      }
      return true;
    };
  },
};

/**
 * Helper function to normalize validator input (regex or function)
 */
export function normalizeValidator(
  input: ValidatorInput | null | undefined,
): ValidatorFn | null {
  if (!input) return null;
  if (typeof input === 'function') return input;
  if (input instanceof RegExp) {
    return validators.fromRegex(input);
  }
  return null;
}
